import { memo, useEffect, useRef, useState } from "react";
import { ContextMenu, type ContextMenuEntry } from "@/components/common/ContextMenu";
import { ToolPolicyConflictError, updateChatPermissionMode } from "@/lib/api";
import type { PermissionMode } from "@/lib/chat";
import { useChatStore } from "@/stores/useChatStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import { readNewChatPermissionMode, writeNewChatPermissionMode } from "@/lib/chat-permission-mode";

const LABELS: Record<PermissionMode, string> = { acceptEdits: "自動承認", manual: "手動確認" };

/** チャット作成者がアーカイブ済みでない会話の確認モードを変更する選択。実行中も変更できる。 */
export const ChatPermissionModeSelector = memo(function ChatPermissionModeSelector() {
	const chatId = useChatStore((s) => s.chatId);
	const permissionMode = useChatStore((s) => s.permissionMode);
	const createdById = useChatStore((s) => s.chatCreatedById);
	const revision = useChatStore((s) => s.revision);
	const archived = useChatStore((s) => s.chatArchived);
	const currentUserId = useCurrentUserStore((s) => s.id);
	const [saving, setSaving] = useState(false);
	const [menuOpen, setMenuOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (chatId === null) useChatStore.getState().setPermissionMode(readNewChatPermissionMode());
	}, [chatId]);

	const disabledReason = !chatId
		? null
		: createdById !== currentUserId
			? "チャット作成者のみ変更できます"
			: archived
				? "アーカイブ済みのチャットでは変更できません"
				: null;

	const disabled = disabledReason !== null || saving;
	const closeMenu = () => setMenuOpen(false);

	const change = async (next: PermissionMode) => {
		if (next === permissionMode || disabled) return;
		setSaving(true);
		try {
			if (!chatId) {
				writeNewChatPermissionMode(next);
				useChatStore.getState().setPermissionMode(next);
				return;
			}
			const metadata = await updateChatPermissionMode(chatId, next, revision);
			if (useChatStore.getState().chatId === chatId)
				useChatStore.getState().setChatMetadata(metadata);
		} catch (error: unknown) {
			// 応答前にチャットを切り替えていた場合、別チャットの revision を汚染しないようガードする。
			if (
				error instanceof ToolPolicyConflictError &&
				error.revision !== null &&
				useChatStore.getState().chatId === chatId
			) {
				useChatStore.getState().setChatRevision(error.revision);
			}
			const message = error instanceof Error ? error.message : "確認モードを変更できませんでした。";
			useToastStore.getState().show({ type: "error", message });
			throw error;
		} finally {
			setSaving(false);
		}
	};
	const menuItems: ContextMenuEntry[] = (Object.keys(LABELS) as PermissionMode[]).map((mode) => ({
		key: mode,
		label: LABELS[mode],
		selectable: true,
		selected: mode === permissionMode,
		testId: `chat-permission-mode-selector-item-${mode}`,
		onSelect: () => {
			void change(mode).catch((error: unknown) => {
				console.error("確認モードの変更に失敗しました", error);
			});
		},
	}));

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				data-testid="chat-permission-mode-selector"
				disabled={disabled}
				title={disabledReason ?? undefined}
				onClick={() => setMenuOpen((open) => !open)}
				className="izanagi-dropdown-trigger"
				data-size="compact"
			>
				{LABELS[permissionMode]}
			</button>
			{menuOpen && triggerRef.current && (
				<ContextMenu
					position={{ anchor: triggerRef.current, align: "right" }}
					onClose={closeMenu}
					testId="chat-permission-mode-selector-menu"
					checkAlign="trailing"
					minWidth={140}
					items={menuItems}
				/>
			)}
		</>
	);
});
